<template>
  <div
    class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-lg dark:shadow-gray-900/50 transition-shadow cursor-pointer"
    @click="$emit('view-details', employee)"
  >
    <div class="p-3 sm:p-5">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div
            class="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center"
          >
            <span class="text-xs sm:text-sm font-medium text-white">
              {{ getInitials(employee.name) }}
            </span>
          </div>
        </div>
        <div class="ml-3 sm:ml-4 flex-1 min-w-0">
          <div class="flex items-center justify-between">
            <h3 class="text-base sm:text-lg font-medium text-gray-900 dark:text-white truncate">
              {{ employee.name }}
            </h3>
            <div class="flex items-center" :title="contractStatusMessage">
              <div class="h-2 w-2 rounded-full" :class="statusDotColor"></div>
            </div>
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ employee.function || 'Geen functie' }}
          </p>
          <p v-if="employee.aanspreekpunt_name" class="text-xs text-gray-500 dark:text-gray-400">
            Aanspreekpunt: {{ employee.aanspreekpunt_name }}
          </p>
          <p
            class="text-xs text-gray-500 dark:text-gray-400"
            v-if="employee.min_billability !== undefined || employee.max_billability !== undefined"
          >
            Declarabiliteitstarget: {{ employee.min_billability || 0 }} -
            {{ employee.max_billability || 0 }}%
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            FTE {{ parseFloat(employee.fte_ratio) || 1.0 }}
          </p>
          <p v-if="isNotYetStarted" class="text-xs" :class="statusTextColor">
            In dienst vanaf: {{ formatContractEndDate(contractStartDate) }}
          </p>
          <p v-else-if="contractEndDate" class="text-xs" :class="statusTextColor">
            Uit dienst: {{ formatContractEndDate(contractEndDate) }}
          </p>
        </div>
      </div>

      <div class="mt-4" v-if="employee.billability_percentage !== undefined">
        <div
          class="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2"
        >
          <span>Bruto declarabiliteit van jouw datumselectie</span>
          <span> {{ billabilityPercentage }}% </span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            class="h-2 rounded-full"
            :style="{
              width: billabilityPercentage + '%',
              backgroundColor: billabilityColor
            }"
          ></div>
        </div>
      </div>

      <div class="mt-4" v-if="employee.teams && employee.teams.length > 0">
        <div class="flex flex-wrap gap-1">
          <span
            v-for="team in displayedTeams"
            :key="team.simplicate_id"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
          >
            {{ team.name }}
          </span>
          <span
            v-if="remainingTeamsCount > 0"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            +{{ remainingTeamsCount }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useContractStatus } from '../../../composables/domain/useContractStatus.js'

const {
  getStatusDotColor,
  getStatusTextColor,
  formatContractEndDate,
  getContractStatus,
  getStatusMessage,
  STATUS
} = useContractStatus()

const props = defineProps({
  employee: {
    type: Object,
    required: true
  }
})

defineEmits(['view-details'])

const contractEndDate = computed(() => props.employee.contract_end_date)
const contractStartDate = computed(() => props.employee.contract_start_date)
const contractStatus = computed(() =>
  getContractStatus(contractEndDate.value, contractStartDate.value)
)
const statusDotColor = computed(() =>
  getStatusDotColor(contractEndDate.value, contractStartDate.value)
)
const statusTextColor = computed(() =>
  getStatusTextColor(contractEndDate.value, contractStartDate.value)
)
const isNotYetStarted = computed(() => contractStatus.value === STATUS.NOT_YET_STARTED)
const contractStatusMessage = computed(() =>
  getStatusMessage(contractEndDate.value, contractStartDate.value)
)

const getInitials = name => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const displayedTeams = computed(() => {
  if (!props.employee.teams) return []
  return props.employee.teams.slice(0, 2)
})

const remainingTeamsCount = computed(() => {
  if (!props.employee.teams) return 0
  return Math.max(0, props.employee.teams.length - 2)
})

const billabilityPercentage = computed(() => props.employee.billability_percentage || 0)

const billabilityColor = '#16a34a'
</script>
